import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Alert, Chip,
  Divider, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, LinearProgress, Switch, FormControlLabel, IconButton,
  Tooltip, CircularProgress, MenuItem, Select, FormControl, InputLabel,
  Snackbar, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  Psychology, CloudUpload, Search, Delete, Refresh, Settings, Storage,
  CheckCircle, Error as ErrorIcon, Warning, Info, Visibility, VisibilityOff,
  GetApp, SettingsBackupRestore, Code, Mic, VolumeUp, Speed, Link, LinkOff,
  SmartToy, Send, Save,
} from '@mui/icons-material';
import { useAppSelector } from '../../hooks/useAppHooks';
import { addEventListener, removeEventListener, emitEvent } from '../../utils/socketClient';
// v6.3.26: static import — the previous dynamic import() of apiClient
// created a lazy chunk; a failed chunk fetch (stale index.html behind
// nginx) blanked the page on first use.
import apiClient from '../../utils/apiClient';

interface TabPanelProps { children?: React.ReactNode; index: number; value: number; }
const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index} style={{ paddingTop: 16 }}>
    {value === index && children}
  </div>
);

const SectionTitle: React.FC<{ icon: React.ReactNode; title: string; subtitle?: string }> = ({ icon, title, subtitle }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
    {icon}
    <Box>
      <Typography variant="h6" fontWeight={600}>{title}</Typography>
      {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
    </Box>
  </Box>
);

// Helper: send request to Jetson through cloud_bridge_node
let _requestId = 0;
const pendingRequests = new Map<string, { resolve: (v: any) => void; reject: (e: any) => void; timer: NodeJS.Timeout }>();

const jetsonRequest = (robotSerial: string, endpoint: string, method = 'GET', body: any = null): Promise<any> => {
  return new Promise((resolve, reject) => {
    const id = `jr_${++_requestId}_${Date.now()}`;
    const timer = setTimeout(() => {
      pendingRequests.delete(id);
      reject(new Error('Jetson request timed out (15s)'));
    }, 15000);
    pendingRequests.set(id, { resolve, reject, timer });
    emitEvent('jetson_request', { request_id: id, robot_serial: robotSerial, endpoint, method, body });
  });
};

const VoiceAI: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const { robotsOnline } = useAppSelector((state) => state.websocket);
  const [tab, setTab] = useState(0);
  useEffect(() => { if (tab === 3) loadAnalytics(); /* eslint-disable-line */ }, [tab]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'info' });
  const showMsg = (message: string, severity: 'success' | 'error' | 'info' = 'success') => setSnackbar({ open: true, message, severity });

  // Robot selection
  const [robots, setRobots] = useState<any[]>([]);
  const [selectedRobot, setSelectedRobot] = useState('');

  // Jetson state
  const [jetsonConnected, setJetsonConnected] = useState(false);
  const [jetsonLoading, setJetsonLoading] = useState(false);
  const [health, setHealth] = useState<any>(null);
  const [config, setConfig] = useState<any>(null);

  // KB state
  const [kbCount, setKbCount] = useState<any>(null);
  const [serverKb, setServerKb] = useState<any[]>([]);
  const loadServerKb = useCallback(async () => {
    if (!selectedRobot) return;
    try {
      const r = await apiClient.get('/admin/voiceai/kb', { params: { robot_serial: selectedRobot } });
      setServerKb(r.data?.rows || []);
    } catch { /* ignore */ }
  }, [selectedRobot]);
  useEffect(() => { loadServerKb(); }, [loadServerKb]);
  const [kbSources, setKbSources] = useState<any[]>([]);
  const [kbSearchQuery, setKbSearchQuery] = useState('');
  const [kbSearchResult, setKbSearchResult] = useState<any>(null);
  const [kbSearching, setKbSearching] = useState(false);
  const [kbUploading, setKbUploading] = useState(false);
  const [kbCategory, setKbCategory] = useState('general');
  const [kbBackups, setKbBackups] = useState<any[]>([]);
  const [kbAddText, setKbAddText] = useState('');
  const [kbAddSource, setKbAddSource] = useState('dashboard');

  // Analytics state (v6.3.22 — Jetson conversations/usage/speakers)
  const [convos, setConvos] = useState<any[]>([]);
  const [speakers, setSpeakers] = useState<any[]>([]);
  const [anLoading, setAnLoading] = useState(false);
  // v6.3.32: log filters + newest-first default
  const [logSearch, setLogSearch] = useState('');
  const [logHandler, setLogHandler] = useState('all');
  const [logNewestFirst, setLogNewestFirst] = useState(true);
  const [convoSource, setConvoSource] = useState<string>('');
  const loadAnalytics = useCallback(async () => {
    if (!selectedRobot) return;
    setAnLoading(true);
    try {
      // v6.3.34: conversations now come from the backend, which mirrors the
      // Jetson log into Mongo and falls back to that mirror when the robot is
      // offline. Speakers/usage remain live-only (they're current-state, not
      // history) and simply stay empty when unreachable.
      const [c, sp, usage] = await Promise.all([
        apiClient.get(`/admin/voiceai/conversations?robot_serial=${encodeURIComponent(selectedRobot)}&limit=100`)
          .then(r => r.data).catch(() => null),
        jetsonRequest(selectedRobot, '/speakers').catch(() => null),
        jetsonRequest(selectedRobot, '/agent/usage').catch(() => null),
      ]);
      setConvos(c?.items || []);
      setConvoSource(c?.source === 'cache' ? `cached${c?.cached_at ? ` · last synced ${new Date(c.cached_at).toLocaleString()}` : ''}` : '');
      setSpeakers(Array.isArray(sp) ? sp : sp?.speakers || []);
      if (usage) setAgentUsage(usage);
    } finally { setAnLoading(false); }
  }, [selectedRobot]);

  // v6.3.32: distinct handlers for the filter dropdown
  const logHandlers = useMemo(() => {
    const set = new Set<string>();
    convos.forEach((c: any) => { const h = c.handler || c.intent; if (h) set.add(h); });
    return Array.from(set).sort();
  }, [convos]);

  // v6.3.32: filtered + sorted (newest-first by default) view.
  const displayConvos = useMemo(() => {
    const ts = (c: any) => {
      const t = c.time || c.timestamp;
      const n = t ? Date.parse(t) : NaN;
      return isNaN(n) ? null : n;
    };
    let rows = convos.map((c: any, i: number) => ({ c, i }));
    const q = logSearch.trim().toLowerCase();
    if (q) {
      rows = rows.filter(({ c }) =>
        `${c.query || c.user || ''} ${c.response || c.assistant || ''} ${c.speaker || c.speaker_name || ''}`
          .toLowerCase().includes(q));
    }
    if (logHandler !== 'all') {
      rows = rows.filter(({ c }) => (c.handler || c.intent) === logHandler);
    }
    rows.sort((a, b) => {
      const ta = ts(a.c), tb = ts(b.c);
      // Fall back to original index when timestamps aren't parseable
      const va = ta ?? a.i, vb = tb ?? b.i;
      return logNewestFirst ? (vb as number) - (va as number) : (va as number) - (vb as number);
    });
    return rows.map(r => r.c);
  }, [convos, logSearch, logHandler, logNewestFirst]);

  // Agent state
  const [agentStatus, setAgentStatus] = useState<any>(null);
  const [agentUsage, setAgentUsage] = useState<any>(null);
  const [apiKeyDialog, setApiKeyDialog] = useState({ open: false, provider: '', key: '' });
  const [showKey, setShowKey] = useState(false);
  const [testingProvider, setTestingProvider] = useState('');

  // Bridge state
  const [bridgeStatus, setBridgeStatus] = useState<any>(null);
  const [bridgeIpcIp, setBridgeIpcIp] = useState('');
  const [bridgeDomainId, setBridgeDomainId] = useState(0);

  // Listen for Jetson responses
  useEffect(() => {
    const handleResponse = (data: any) => {
      const pending = pendingRequests.get(data.request_id);
      if (pending) {
        clearTimeout(pending.timer);
        pendingRequests.delete(data.request_id);
        if (data.error) {
          pending.reject(new Error(data.error));
        } else {
          pending.resolve(data.data);
        }
      }
    };
    addEventListener('jetson_response', handleResponse);
    return () => removeEventListener('jetson_response', handleResponse);
  }, []);

  // Load robots list
  useEffect(() => {
    const loadRobots = async () => {
      try {
          const res = await apiClient.get('/robots');
        setRobots(res.data || []);
        if (res.data?.length > 0 && !selectedRobot) {
          // Select first online robot, or first robot
          const onlineRobot = res.data.find((r: any) => robotsOnline.includes(r.serial_number));
          setSelectedRobot((onlineRobot || res.data[0]).serial_number);
        }
      } catch { /* ignore */ }
    };
    loadRobots();
  }, [robotsOnline]); // eslint-disable-line

  // Check Jetson health when robot changes
  const checkJetson = useCallback(async () => {
    if (!selectedRobot || !robotsOnline.includes(selectedRobot)) {
      setJetsonConnected(false);
      setHealth(null);
      return;
    }
    setJetsonLoading(true);
    try {
      const h = await jetsonRequest(selectedRobot, '/health');
      setHealth(h);
      setJetsonConnected(true);
      // v6.3.25: Status tile showed '—' because kb/count only loaded on
      // the KB tab — fetch it with health.
      jetsonRequest(selectedRobot, '/kb/count').then(setKbCount).catch(() => {});
    } catch {
      setJetsonConnected(false);
      setHealth(null);
    }
    setJetsonLoading(false);
  }, [selectedRobot, robotsOnline]);

  useEffect(() => { checkJetson(); }, [checkJetson]);

  // Load data for current tab
  const loadTabData = useCallback(async () => {
    if (!jetsonConnected || !selectedRobot) return;
    try {
      if (tab === 0) {
        const [c, h] = await Promise.all([
          jetsonRequest(selectedRobot, '/config').catch(() => null),
          jetsonRequest(selectedRobot, '/health').catch(() => null),
        ]);
        setConfig(c);
        if (h) setHealth(h);
      } else if (tab === 1) {
        const [count, sources, backups] = await Promise.all([
          jetsonRequest(selectedRobot, '/kb/count').catch(() => null),
          jetsonRequest(selectedRobot, '/kb/sources').catch(() => null),
          jetsonRequest(selectedRobot, '/kb/backups').catch(() => null),
        ]);
        setKbCount(count);
        setKbSources(sources?.sources || []);
        setKbBackups(backups?.backups || backups || []);
      } else if (tab === 2) {
        const [status, usage] = await Promise.all([
          jetsonRequest(selectedRobot, '/agent/status').catch(() => null),
          jetsonRequest(selectedRobot, '/agent/usage').catch(() => null),
        ]);
        setAgentStatus(status);
        setAgentUsage(usage);
      } else if (tab === 3) {
        const bs = await jetsonRequest(selectedRobot, '/bridge/status').catch(() => null);
        setBridgeStatus(bs);
        if (bs?.ipc_ip) setBridgeIpcIp(bs.ipc_ip);
      }
    } catch { /* ignore */ }
  }, [jetsonConnected, selectedRobot, tab]);

  useEffect(() => { loadTabData(); }, [loadTabData]);

  const isRobotOnline = selectedRobot && robotsOnline.includes(selectedRobot);

  // KB actions
  const handleKbSearch = async () => {
    if (!kbSearchQuery.trim()) return;
    setKbSearching(true);
    try {
      const result = await jetsonRequest(selectedRobot, '/kb/search', 'POST', { query: kbSearchQuery });
      setKbSearchResult(result);
    } catch (e: any) {
      showMsg(e.message, 'error');
    }
    setKbSearching(false);
  };

  const handleKbAddText = async () => {
    if (!kbAddText.trim()) return;
    try {
      // v6.3.26: goes through the server library — durable copy first,
      // then pushed to the robot; push failures are reported, not silent.
      const r = await apiClient.post('/admin/voiceai/kb/add-text', {
        robot_serial: selectedRobot, text: kbAddText,
        source: kbAddSource || undefined, category: kbCategory,
      });
      showMsg(r.data?.message || 'Text added', r.data?.doc?.push_ok ? 'success' : 'info');
      setKbAddText('');
      loadServerKb();
      loadTabData();
    } catch (e: any) {
      showMsg(e?.response?.data?.detail || e.message || 'Add failed', 'error');
    }
  };

  const handleDeleteSource = async (source: string) => {
    if (!window.confirm(`Delete source "${source}" and all its chunks from the ROBOT?\n\n` +
      `The server library keeps its copy unless you choose to remove that too.`)) return;
    try {
      await jetsonRequest(selectedRobot, '/kb/delete_source', 'DELETE', { source });
      showMsg(`Deleted source from robot: ${source}`);
      // v6.3.31: offer to remove the server copy as well, so the library
      // doesn't quietly resurrect the document on the next Restore All.
      const lib = serverKb.find((d: any) => d.source === source);
      if (lib && window.confirm(`Also delete "${source}" from the SERVER library?\n\n` +
        `Cancel = keep it on the server (you can push it back later).`)) {
        await apiClient.delete(`/admin/voiceai/kb/${lib.id}`).catch(() => {});
      }
      loadServerKb();
      loadTabData();
    } catch (e: any) { showMsg(e.message || 'Delete failed', 'error'); }
  };

  const handleDeleteBackup = async (name: string) => {
    if (!window.confirm(`Delete backup ${name} from the robot? This cannot be undone.`)) return;
    try {
      const r = await jetsonRequest(selectedRobot, `/kb/backups/${encodeURIComponent(name)}`, 'DELETE');
      showMsg(r?.message || `Deleted ${name}`);
      loadTabData();
    } catch (e: any) { showMsg(e.message || 'Delete failed', 'error'); }
  };

  const handleKbClear = async () => {
    if (!window.confirm('Clear ALL knowledge base documents? This cannot be undone.')) return;
    try {
      await jetsonRequest(selectedRobot, '/kb/clear', 'DELETE');
      showMsg('Knowledge base cleared');
      loadTabData();
    } catch (e: any) { showMsg(e.message, 'error'); }
  };

  const handleKbBackup = async () => {
    try {
      await jetsonRequest(selectedRobot, '/kb/backup', 'POST');
      showMsg('KB backup created');
      loadTabData();
    } catch (e: any) { showMsg(e.message, 'error'); }
  };

  const handleKbRestore = async (name: string) => {
    if (!window.confirm(`Restore KB from "${name}"?`)) return;
    try {
      await jetsonRequest(selectedRobot, '/kb/restore', 'POST', { name });
      showMsg('KB restored');
      loadTabData();
    } catch (e: any) { showMsg(e.message, 'error'); }
  };

  // Agent actions
  const handleSetKey = async () => {
    try {
      await jetsonRequest(selectedRobot, '/agent/set_key', 'POST', { provider: apiKeyDialog.provider, api_key: apiKeyDialog.key });
      showMsg(`API key set for ${apiKeyDialog.provider}`);
      setApiKeyDialog({ open: false, provider: '', key: '' });
      loadTabData();
    } catch (e: any) { showMsg(e.message, 'error'); }
  };

  const handleTestProvider = async (provider: string) => {
    setTestingProvider(provider);
    try {
      const result = await jetsonRequest(selectedRobot, '/agent/test', 'POST', { provider, message: 'Hello, are you working?' });
      showMsg(`${provider}: ${result?.response || result?.message || 'OK'}`);
    } catch (e: any) { showMsg(`${provider} test failed: ${e.message}`, 'error'); }
    setTestingProvider('');
  };

  const handleToggleProvider = async (provider: string, enabled: boolean) => {
    try {
      await jetsonRequest(selectedRobot, '/agent/enable', 'POST', { provider, enabled });
      loadTabData();
    } catch (e: any) { showMsg(e.message, 'error'); }
  };

  const handleSetPriority = async (priority: string[]) => {
    try {
      await jetsonRequest(selectedRobot, '/agent/set_priority', 'POST', { priority });
      showMsg('Provider priority updated');
      loadTabData();
    } catch (e: any) { showMsg(e.message, 'error'); }
  };

  // Bridge actions
  const handleBridgeEnable = async () => {
    try {
      await jetsonRequest(selectedRobot, '/bridge/enable', 'POST', { ipc_ip: bridgeIpcIp, domain_id: bridgeDomainId });
      showMsg('ROS2 bridge enabled');
      loadTabData();
    } catch (e: any) { showMsg(e.message, 'error'); }
  };

  const handleBridgeDisable = async () => {
    try {
      await jetsonRequest(selectedRobot, '/bridge/disable', 'POST');
      showMsg('ROS2 bridge disabled');
      loadTabData();
    } catch (e: any) { showMsg(e.message, 'error'); }
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        message={snackbar.message} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Psychology sx={{ fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" fontWeight={700}>Voice AI</Typography>
            <Typography variant="body2" color="text.secondary">Devibot Jetson AI Module Management</Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Robot</InputLabel>
            <Select value={selectedRobot} label="Robot" onChange={(e) => setSelectedRobot(e.target.value)}>
              {robots.map((r: any) => (
                <MenuItem key={r.serial_number} value={r.serial_number}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: robotsOnline.includes(r.serial_number) ? 'success.main' : 'text.disabled' }} />
                    {r.serial_number}{r.friendly_name ? ` (${r.friendly_name})` : ''}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Chip
            icon={jetsonConnected ? <CheckCircle /> : <ErrorIcon />}
            label={jetsonLoading ? 'Checking...' : jetsonConnected ? 'Jetson Online' : !isRobotOnline ? 'Robot Offline' : 'Jetson Offline'}
            color={jetsonConnected ? 'success' : 'default'}
            variant="outlined"
          />
        </Box>
      </Box>

      {/* Not connected warning */}
      {!isRobotOnline && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Selected robot is offline. Connect the robot to manage its Jetson AI module.
        </Alert>
      )}

      {isRobotOnline && !jetsonConnected && !jetsonLoading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Robot is online but Jetson AI module is not detected. The Jetson may not be connected or the KB API may not be running.
          This does not affect other robot functions.
        </Alert>
      )}

      {/* v6.3.35: make it explicit what still works while the Jetson is
          unreachable, instead of silently greying the whole page out. */}
      {!jetsonConnected && !jetsonLoading && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Jetson unreachable — showing server-stored data.</strong>{' '}
          Knowledge Base library and Analytics history are held on the fleet
          server and remain available. Actions that write to the robot (Push,
          Sync, Restore, KB search) and the AI Agent tab need the robot online.
        </Alert>
      )}

      {/* Tabs */}
      <Card>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab icon={<Settings sx={{ fontSize: 18 }} />} iconPosition="start" label="Status" />
          {/* v6.3.35: Knowledge Base and Analytics read from the FLEET SERVER
              (KB library table / mirrored conversation cache), not the Jetson,
              so they stay usable while the robot is offline. Previously all
              three were gated on jetsonConnected, which greyed out the entire
              page — including data the server already had. Only the AI Agent
              tab is pure live Jetson state, so it remains gated. */}
          <Tab icon={<Storage sx={{ fontSize: 18 }} />} iconPosition="start" label="Knowledge Base" />
          <Tab icon={<SmartToy sx={{ fontSize: 18 }} />} iconPosition="start" label="AI Agent" disabled={!jetsonConnected} />
          <Tab icon={<Speed sx={{ fontSize: 18 }} />} iconPosition="start" label="Analytics" />
        </Tabs>

        <CardContent sx={{ p: 3 }}>
          {/* ===== STATUS TAB ===== */}
          <TabPanel value={tab} index={0}>
            <SectionTitle icon={<Info color="primary" />} title="Jetson Module Status"
              subtitle="Connection status and system overview" />

            {health && (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2, mb: 3 }}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <CheckCircle sx={{ color: 'success.main', fontSize: 32, mb: 1 }} />
                  <Typography variant="body2" fontWeight={600}>API Status</Typography>
                  <Typography variant="caption" color="text.secondary">{health.status || 'OK'}</Typography>
                </Paper>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Mic sx={{ color: 'primary.main', fontSize: 32, mb: 1 }} />
                  <Typography variant="body2" fontWeight={600}>Voice AI</Typography>
                  <Typography variant="caption" color="text.secondary">Active</Typography>
                </Paper>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <VolumeUp sx={{ color: 'primary.main', fontSize: 32, mb: 1 }} />
                  <Typography variant="body2" fontWeight={600}>KB Documents</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {kbCount?.count ?? health?.kb_chunks ?? '—'}
                    {health?.kb_sources != null && ` chunks · ${health.kb_sources} sources`}
                  </Typography>
                </Paper>
              </Box>
            )}

            {/* v6.3.26: extra detail — all of it comes from the SAME
                /health response already fetched above, so no additional
                requests and no extra CPU on the robot. */}
            {health && (
              <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                <Table size="small">
                  <TableBody>
                    {[
                      ['Version', health.version],
                      ['LLM model', health.llm || health.model],
                      ['Cloud AI', health.cloud_ai_enabled === undefined ? undefined : (health.cloud_ai_enabled ? 'enabled' : 'local only')],
                      ['Providers', Array.isArray(health.providers) ? health.providers.join(', ') : health.providers],
                      ['Whisper model', health.whisper || health.whisper_model],
                      ['Auth', health.auth_required ? `token required (…${health.auth_fingerprint || ''})` : 'open'],
                      ['KB error', health.kb_error],
                      ['Robot serial', selectedRobot],
                    ].filter(([, val]) => val !== undefined && val !== null && val !== '')
                      .map(([label, val]: any) => (
                        <TableRow key={label}>
                          <TableCell sx={{ width: 180, color: 'text.secondary' }}>{label}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{String(val)}</TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {config && (
              <>
                <Typography variant="subtitle2" gutterBottom>System Configuration</Typography>
                <Paper variant="outlined" sx={{ p: 2, maxHeight: 300, overflow: 'auto', fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
                  {typeof config === 'object' ? JSON.stringify(config, null, 2) : String(config)}
                </Paper>
              </>
            )}

            {!jetsonConnected && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Psychology sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">Jetson Not Connected</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {!isRobotOnline ? 'Connect the robot first, then the Jetson module.' : 'Ensure the KB API is running on the Jetson (port 8080).'}
                </Typography>
                <Button variant="outlined" sx={{ mt: 2 }} onClick={checkJetson} startIcon={<Refresh />}>
                  Retry Connection
                </Button>
              </Box>
            )}
          </TabPanel>

          {/* ===== KNOWLEDGE BASE TAB ===== */}
          <TabPanel value={tab} index={1}>
            <SectionTitle icon={<Storage color="primary" />} title="Knowledge Base"
              subtitle="Manage documents, search, and backup the AI knowledge base" />

            {/* Stats */}
            {kbCount && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Knowledge base contains <strong>{kbCount.count}</strong> indexed chunks from <strong>{kbSources.length}</strong> sources.
              </Alert>
            )}

            {/* ── v6.3.25: Server KB Library — upload files/text, stored in
                the fleet DB first, then pushed to the Jetson. Survives
                Jetson re-images (Restore All). ── */}
            <Typography variant="subtitle2" sx={{ mt: 3 }}>
              Server KB Library (stored on this server, pushed to the robot)
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Documents uploaded from the webapp. They live in the fleet database so a
              re-imaged Jetson can be restored with one click. Documents added directly on
              the robot appear under "Sources" below, not here.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button variant="outlined" size="small" component="label">
                Upload File
                <input hidden type="file" accept=".txt,.md,.csv,.json,.yaml,.yml,.rst,.html"
                  onChange={async (e) => {
                    const f = e.target.files?.[0]; if (!f) return;
                    const fd = new FormData();
                    fd.append('robot_serial', selectedRobot);
                    fd.append('category', 'general');
                    fd.append('file', f);
                    try {
                          const r = await apiClient.post('/admin/voiceai/kb/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                      setSnackbar({ open: true, message: r.data?.message || 'Uploaded', severity: r.data?.doc?.push_ok ? 'success' : 'info' });
                      loadServerKb();
                    } catch (err: any) {
                      setSnackbar({ open: true, message: err?.response?.data?.detail || 'Upload failed', severity: 'error' });
                    }
                    e.target.value = '';
                  }} />
              </Button>
              <Button variant="outlined" size="small" onClick={async () => {
                try {
                  const fd = new FormData(); fd.append('robot_serial', selectedRobot);
                  const r = await apiClient.post('/admin/voiceai/kb/restore', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                  setSnackbar({ open: true, message: r.data?.message, severity: r.data?.success ? 'success' : 'info' });
                } catch { setSnackbar({ open: true, message: 'Restore failed', severity: 'error' }); }
              }} disabled={!jetsonConnected}
                title={!jetsonConnected ? "Requires the robot to be online" : undefined}>Restore All to Robot</Button>
              <Button size="small" variant="outlined" startIcon={<CloudUpload />} onClick={async () => {
                try {
                  const fd = new FormData(); fd.append('robot_serial', selectedRobot);
                  const r = await apiClient.post('/admin/voiceai/kb/sync', fd,
                    { headers: { 'Content-Type': 'multipart/form-data' } });
                  showMsg(r.data?.message || 'Synced', r.data?.success ? 'success' : 'error');
                  loadServerKb();
                } catch (e: any) { showMsg(e?.response?.data?.detail || 'Sync failed', 'error'); }
              }} disabled={!jetsonConnected}
                title={!jetsonConnected ? "Requires the robot to be online" : undefined}>Sync from Robot</Button>
              <Button size="small" startIcon={<Refresh />} onClick={() => loadServerKb()}>Refresh</Button>
              <Button size="small" variant="outlined" startIcon={<GetApp />} onClick={async () => {
                try {
                  const r = await apiClient.get('/admin/voiceai/kb/export', { params: { robot_serial: selectedRobot } });
                  if (!r.data?.count) {
                    showMsg('Server KB library is empty for this robot — upload a document first', 'info');
                    return;
                  }
                  if (r.data?.note) showMsg(r.data.note, 'info');
                  const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' });
                  const a = document.createElement('a');
                  a.href = URL.createObjectURL(blob);
                  a.download = `kb_library_${selectedRobot}_${new Date().toISOString().slice(0, 10)}.json`;
                  a.click(); URL.revokeObjectURL(a.href);
                } catch { showMsg('Export failed', 'error'); }
              }}>Download All</Button>
            </Box>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 260, mb: 2 }}>
              <Table size="small" stickyHeader>
                <TableHead><TableRow>
                  <TableCell>Source</TableCell><TableCell>Origin</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">Chars</TableCell><TableCell>Pushed</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {serverKb.map((d: any) => (
                    <TableRow key={d.id} hover>
                      <TableCell>{d.source}</TableCell>
                      <TableCell>
                        <Chip size="small" variant="outlined"
                          label={d.origin === 'robot' ? 'from robot' : 'webapp'} />
                      </TableCell>
                      <TableCell>{d.category}</TableCell>
                      <TableCell align="right">{d.chars}</TableCell>
                      <TableCell>{d.push_ok
                        ? <Chip size="small" color="success" label="pushed" />
                        : <Tooltip title={d.push_error || 'not pushed'}>
                            <Chip size="small" color="warning"
                              label={(d.push_error || 'pending').slice(0, 42)} /></Tooltip>}
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={async () => {
                                  await apiClient.post(`/admin/voiceai/kb/${d.id}/push`).catch(() => {});
                          loadServerKb();
                        }} disabled={!jetsonConnected}
                          title={!jetsonConnected ? "Requires the robot to be online" : undefined}>Push</Button>
                        <IconButton size="small" onClick={async () => {
                                  await apiClient.delete(`/admin/voiceai/kb/${d.id}`).catch(() => {});
                          loadServerKb();
                        }}><Delete fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {serverKb.length === 0 && (
                    <TableRow><TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary">No documents in the server library yet.</Typography>
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Search */}
            <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
              <TextField fullWidth size="small" placeholder="Search knowledge base..." value={kbSearchQuery}
                onChange={(e) => setKbSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleKbSearch()}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} />
              <Button variant="contained" onClick={handleKbSearch} disabled={kbSearching || !jetsonConnected}
                startIcon={kbSearching ? <CircularProgress size={16} /> : <Search />}>Search</Button>
            </Box>

            {kbSearchResult && (
              <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'rgba(0,0,0,0.02)' }}>
                <Typography variant="subtitle2" gutterBottom>Search Result {kbSearchResult.score && `(score: ${kbSearchResult.score.toFixed(3)})`}</Typography>
                <Typography variant="body2">{kbSearchResult.found ? kbSearchResult.text : 'No results found.'}</Typography>
              </Paper>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Add Text */}
            <Typography variant="subtitle2" gutterBottom>Add Text to KB</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <TextField fullWidth size="small" multiline rows={2} placeholder="Enter text to add to knowledge base..."
                value={kbAddText} onChange={(e) => setKbAddText(e.target.value)} />
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Category</InputLabel>
                <Select value={kbCategory} label="Category" onChange={(e) => setKbCategory(e.target.value)}>
                  {['general', 'safety', 'operations', 'factory_floor', 'warehouse', 'electronics'].map(c => (
                    <MenuItem key={c} value={c}>{c}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="contained" onClick={handleKbAddText} startIcon={<Send />}>Add</Button>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Sources */}
            {kbSources.length > 0 && (
              <>
                <Typography variant="subtitle2" gutterBottom>Sources</Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, maxHeight: 200 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Source</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell align="right">Chunks</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {kbSources.map((s: any, i: number) => (
                        <TableRow key={i} hover>
                          <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{s.source}</Typography></TableCell>
                          <TableCell><Chip label={s.category || 'general'} size="small" variant="outlined" /></TableCell>
                          <TableCell align="right">{s.chunks}</TableCell>
                          <TableCell align="right">
                            <Tooltip title="Delete this source and all its chunks from the robot">
                              <IconButton size="small" color="error" onClick={() => handleDeleteSource(s.source)}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Backup & Actions */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button size="small" variant="outlined" startIcon={<CloudUpload />} onClick={handleKbBackup}>Backup KB</Button>
              <Button size="small" variant="outlined" color="error" startIcon={<Delete />} onClick={handleKbClear}>Clear All</Button>
              <Button size="small" variant="outlined" startIcon={<Refresh />} onClick={loadTabData}>Refresh</Button>
            </Box>

            {kbBackups.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2">Backups</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  KB snapshots stored on the robot's Jetson (created by the "Backup KB"
                  button here or on the robot console). Click to restore · trash to delete.
                </Typography>
                {(Array.isArray(kbBackups) ? kbBackups : []).map((b: any, i: number) => {
                  const nm = typeof b === 'string' ? b : (b.name || b.filename);
                  const sz = typeof b === 'object' && b.size_mb ? ` · ${b.size_mb} MB` : '';
                  return (
                    <Chip key={i} label={`${nm}${sz}`}
                      onClick={() => handleKbRestore(nm)}
                      onDelete={() => handleDeleteBackup(nm)}
                      deleteIcon={<Delete />}
                      icon={<SettingsBackupRestore />} variant="outlined" sx={{ mr: 1, mb: 1 }} />
                  );
                })}
              </Box>
            )}
          </TabPanel>

          {/* ===== AI AGENT TAB ===== */}
          <TabPanel value={tab} index={2}>
            <SectionTitle icon={<SmartToy color="primary" />} title="AI Agent Configuration"
              subtitle="Manage AI providers, API keys, and usage" />

            {/* Usage Stats */}
            {agentUsage && (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr 1fr' }, gap: 2, mb: 3 }}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h5" fontWeight={700} color="primary">{agentUsage.today?.calls ?? agentUsage.daily_calls ?? 0}</Typography>
                  <Typography variant="caption" color="text.secondary">Calls Today</Typography>
                </Paper>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h5" fontWeight={700} color="primary">{agentUsage.today?.calls_limit ?? agentUsage.daily_calls_limit ?? 'Unlimited'}</Typography>
                  <Typography variant="caption" color="text.secondary">Daily Limit</Typography>
                </Paper>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h5" fontWeight={700} color="primary">${(agentUsage.today?.estimated_cost_usd ?? agentUsage.daily_cost_usd ?? 0).toFixed(4)}</Typography>
                  <Typography variant="caption" color="text.secondary">Cost Today</Typography>
                </Paper>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h5" fontWeight={700} color="primary">{(agentUsage.today ? (agentUsage.today.tokens_in ?? 0) + (agentUsage.today.tokens_out ?? 0) : agentUsage.total_tokens) || 0}</Typography>
                  <Typography variant="caption" color="text.secondary">Tokens Used</Typography>
                </Paper>
              </Box>
            )}

            {/* Providers */}
            {agentStatus?.providers && (
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Provider</TableCell>
                      <TableCell>Model</TableCell>
                      <TableCell>API Key</TableCell>
                      <TableCell>Enabled</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(Array.isArray(agentStatus.providers) ? agentStatus.providers : Object.entries(agentStatus.providers || {}).map(([k, v]: any) => ({ name: k, ...v }))).map((p: any) => (
                      <TableRow key={p.name || p.provider} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{(p.name || p.provider || '').toUpperCase()}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{p.model || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={p.has_key || p.key_set ? 'Set' : 'Not Set'} size="small"
                            color={p.has_key || p.key_set ? 'success' : 'warning'} variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Switch checked={p.enabled !== false} size="small"
                            onChange={(e) => handleToggleProvider(p.name || p.provider, e.target.checked)} />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Set API Key">
                            <IconButton size="small" onClick={() => setApiKeyDialog({ open: true, provider: p.name || p.provider, key: '' })}>
                              <Settings sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Test">
                            <IconButton size="small" onClick={() => handleTestProvider(p.name || p.provider)}
                              disabled={testingProvider === (p.name || p.provider)}>
                              {testingProvider === (p.name || p.provider) ? <CircularProgress size={16} /> : <Send sx={{ fontSize: 16 }} />}
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Priority */}
            {agentStatus?.priority && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Provider Priority</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {agentStatus.priority.map((p: string, i: number) => (
                    <Chip key={p} label={`${i + 1}. ${p}`} variant="outlined" />
                  ))}
                </Box>
              </Box>
            )}

            <Button size="small" variant="outlined" startIcon={<Refresh />} onClick={loadTabData}>Refresh</Button>
          </TabPanel>

          {/* ===== ROS2 BRIDGE TAB ===== */}


          {/* ── Analytics (v6.3.22): conversation history, usage, speakers ── */}
          <TabPanel value={tab} index={3}>
            <SectionTitle icon={<Speed color="primary" />} title="Voice AI Analytics"
              subtitle="Conversation history, cloud usage and enrolled speakers (no live stream)" />
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Button startIcon={<Refresh />} variant="outlined" size="small"
                onClick={loadAnalytics} disabled={anLoading}>Refresh</Button>
              {agentUsage?.today && (
                <Chip label={`Today: ${agentUsage.today.calls ?? 0} calls · ${(agentUsage.today.tokens_in ?? 0) + (agentUsage.today.tokens_out ?? 0)} tokens · $${(agentUsage.today.estimated_cost_usd ?? 0).toFixed(3)}`}
                  color="info" variant="outlined" />
              )}
              <Chip label={`Speakers enrolled: ${speakers.length}`} variant="outlined" />
            </Box>
            {/* v6.3.32: log filters + sort */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField size="small" label="Search query / response / speaker"
                value={logSearch} onChange={(e) => setLogSearch(e.target.value)}
                sx={{ minWidth: 260 }} />
              <TextField size="small" select label="Handler" value={logHandler}
                onChange={(e) => setLogHandler(e.target.value)} sx={{ minWidth: 160 }}>
                <MenuItem value="all">All handlers</MenuItem>
                {logHandlers.map(h => (<MenuItem key={h} value={h}>{h}</MenuItem>))}
              </TextField>
              <Button size="small" variant="outlined"
                onClick={() => setLogNewestFirst(v => !v)}>
                {logNewestFirst ? 'Newest first ↓' : 'Oldest first ↑'}
              </Button>
              <Typography variant="caption" color="text.secondary">
                {displayConvos.length} of {convos.length}
              </Typography>
              {convoSource && (
                <Chip size="small" color="warning" variant="outlined"
                  label={`Robot offline — showing ${convoSource}`} />
              )}
            </Box>
            {anLoading && <LinearProgress sx={{ mb: 2 }} />}
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 460 }}>
              <Table size="small" stickyHeader>
                <TableHead><TableRow>
                  <TableCell>Time</TableCell><TableCell>Speaker</TableCell>
                  <TableCell>Query</TableCell><TableCell>Response</TableCell>
                  <TableCell>Handler</TableCell><TableCell align="right">ms</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {displayConvos.length === 0 && !anLoading && (
                    <TableRow><TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary">
                        {convos.length === 0 && !jetsonConnected
                          ? 'No stored conversations for this robot yet. History is mirrored to the server whenever the robot is online — connect it once to populate the archive.'
                          : convos.length > 0
                            ? 'No conversations match the current filters.'
                            : 'No conversations logged yet.'}
                      </Typography>
                    </TableCell></TableRow>
                  )}
                  {displayConvos.map((c: any, i: number) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{c.time || c.timestamp || ''}</TableCell>
                      <TableCell>{c.speaker || c.speaker_name || '—'}</TableCell>
                      <TableCell sx={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.query || c.user || ''}</TableCell>
                      <TableCell sx={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.response || c.assistant || ''}</TableCell>
                      <TableCell><Chip size="small" variant="outlined" label={c.handler || c.intent || '—'} /></TableCell>
                      <TableCell align="right">{c.latency_ms ?? c.ms ?? ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
        </CardContent>
      </Card>

      {/* API Key Dialog */}
      <Dialog open={apiKeyDialog.open} onClose={() => setApiKeyDialog({ open: false, provider: '', key: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Set API Key — {apiKeyDialog.provider?.toUpperCase()}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="API Key" value={apiKeyDialog.key}
            onChange={(e) => setApiKeyDialog(prev => ({ ...prev, key: e.target.value }))}
            type={showKey ? 'text' : 'password'} sx={{ mt: 1 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowKey(!showKey)} size="small">
                    {showKey ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApiKeyDialog({ open: false, provider: '', key: '' })}>Cancel</Button>
          <Button variant="contained" onClick={handleSetKey} disabled={!apiKeyDialog.key}>Save Key</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VoiceAI;
